import {useCallback, useEffect, useRef, useState} from 'react'
import type {RawAlternative, RawAnchor} from '../api/types'
import {fetchAlternative, fetchAnchors} from '../api/client'
import {assetUrl, num} from '../scene/mapping'
import {ensureMotionPermission, isLikelyARCapable} from '../engine/engine'
import {track} from '../analytics'
import type {ARShareSession} from '../scene/arShareSession'
import {startARShareSession} from '../scene/arShareSession'
import {buildAnchorGroup} from '../scene/anchorGroup'
import type {OrbitViewer} from '../scene/orbitViewer'
import {startOrbitViewer} from '../scene/orbitViewer'
import {SITE_URL} from '../config'

type Phase =
  | 'loading'
  | 'landing'
  | 'starting'
  | 'ar'
  | 'viewer'
  | 'not-found'
  | 'error'

interface Props {
  alternativeId: string
  anchorId: string
  forceViewer: boolean
}

export function ShareView({alternativeId, anchorId, forceViewer}: Props) {
  const [phase, setPhase] = useState<Phase>('loading')
  const [alt, setAlt] = useState<RawAlternative | null>(null)
  const [anchor, setAnchor] = useState<RawAnchor | null>(null)
  const [anchorCount, setAnchorCount] = useState(0)
  const [errorMsg, setErrorMsg] = useState('')
  const [assetProgress, setAssetProgress] = useState<[number, number] | null>(null)
  const [reticleVisible, setReticleVisible] = useState(false)
  const [placed, setPlaced] = useState(false)
  const [tabletop, setTabletop] = useState(false)

  const canvasRef = useRef<HTMLCanvasElement>(null)
  const sessionRef = useRef<ARShareSession | null>(null)
  const viewerRef = useRef<OrbitViewer | null>(null)
  const viewerStartingRef = useRef(false)
  const arStartingRef = useRef(false)

  // Data load
  useEffect(() => {
    let cancelled = false
    Promise.all([fetchAlternative(alternativeId), fetchAnchors(alternativeId)])
      .then(([a, anchors]) => {
        if (cancelled) return
        const target = anchors.find((x) => String(x.id) === String(anchorId))
        setAlt(a)
        setAnchorCount(anchors.length)
        if (!target) {
          setPhase('not-found')
          return
        }
        setAnchor(target)
        setPhase('landing')
        track('share_view', {alternativeId, anchorId, meta: {arCapable: isLikelyARCapable()}})
      })
      .catch((err) => {
        if (cancelled) return
        setErrorMsg(err instanceof Error ? err.message : String(err))
        setPhase('error')
      })
    return () => {
      cancelled = true
    }
  }, [alternativeId, anchorId])

  // Teardown on unmount
  useEffect(
    () => () => {
      sessionRef.current?.stop()
      viewerRef.current?.stop()
    },
    [],
  )

  const startViewer = useCallback(async () => {
    // Reentrancy guard: the camera-failed callback and the startAR catch could
    // otherwise both start a viewer on the same canvas (review finding).
    if (!anchor || !canvasRef.current || viewerRef.current || viewerStartingRef.current) return
    viewerStartingRef.current = true
    setPhase('viewer')
    try {
      const content = await buildAnchorGroup(anchor, (l, t) => setAssetProgress([l, t]))
      setAssetProgress(null)
      viewerRef.current = startOrbitViewer(canvasRef.current, content)
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : String(err))
      setPhase('error')
    } finally {
      viewerStartingRef.current = false
    }
  }, [anchor])

  const startAR = useCallback(async () => {
    if (!anchor || !canvasRef.current || arStartingRef.current) return
    arStartingRef.current = true
    setPhase('starting')
    track('share_ar_started', {alternativeId, anchorId})
    const motionOk = await ensureMotionPermission()
    if (!motionOk) {
      // Denied motion → AR can't track; fall back rather than dead-end (plan §7).
      arStartingRef.current = false
      await startViewer()
      return
    }
    try {
      setPhase('ar')
      // Camera denial rejects this promise quickly (engine fast-fails and stops
      // itself) — the catch below is the ONLY fallback path, so the viewer can't
      // be started twice.
      sessionRef.current = await startARShareSession(canvasRef.current, anchor, {
        onAssetProgress: (l, t) => setAssetProgress(l >= t ? null : ([l, t] as [number, number])),
        // WebXR sessions can be ended by the system back gesture; without this
        // the HUD would keep rendering over a stopped scene.
        onSessionEnd: () => {
          sessionRef.current?.stop()
          sessionRef.current = null
          setPlaced(false)
          setPhase('landing')
        },
        onReticle: setReticleVisible,
        onPlaced: () => {
          setPlaced(true)
          track('share_placed', {alternativeId, anchorId})
        },
        onError: (err) => console.warn('[ar]', err),
      })
    } catch (err) {
      console.warn('[ar] failed to start, falling back to viewer', err)
      sessionRef.current?.stop()
      sessionRef.current = null
      await startViewer()
    } finally {
      arStartingRef.current = false
    }
  }, [anchor, startViewer])

  const toggleTabletop = useCallback(() => {
    setTabletop((prev) => {
      sessionRef.current?.setTabletop(!prev)
      return !prev
    })
  }, [])

  const replace = useCallback(() => {
    sessionRef.current?.replace()
    setPlaced(false)
  }, [])

  if (phase === 'loading') return <div className="center-note">Loading checkpoint…</div>
  if (phase === 'not-found')
    return <div className="center-note">This checkpoint doesn't exist (or was removed).</div>
  if (phase === 'error')
    return (
      <div className="center-note">
        <p>Couldn't load this preview.</p>
        <p className="dim">{errorMsg}</p>
      </div>
    )

  const altName = alt?.public_name || alt?.name || 'an alternative'
  const cover = assetUrl(alt?.main_image_asset?.path)
  const anchorName = anchor?.name || `Checkpoint ${anchor ? num(anchor.order) ?? '' : ''}`

  return (
    <div className="share-view">
      {/* Tap-to-place uses a native listener inside arShareSession — the engine
          re-parents this canvas to document.body, where React's delegated
          handlers can't see events. */}
      <canvas ref={canvasRef} className="ar-canvas" />

      {phase === 'landing' && (
        <div className="landing">
          {cover && <img className="cover" src={cover} alt="" />}
          <div className="landing-body">
            <p className="eyebrow">Checkpoint preview</p>
            <h1>{anchorName}</h1>
            <p className="dim">
              From <strong>{altName}</strong>
              {alt?.tagline ? ` — ${alt.tagline}` : ''}
            </p>
            <button className="btn primary" onClick={isLikelyARCapable() && !forceViewer ? startAR : startViewer}>
              {isLikelyARCapable() && !forceViewer ? 'View in AR' : 'View in 3D'}
            </button>
            {isLikelyARCapable() && !forceViewer && (
              <button className="btn ghost" onClick={startViewer}>
                View in 3D instead
              </button>
            )}
            <p className="fine">
              AR uses your camera and motion sensors — nothing is recorded or uploaded.
            </p>
          </div>
          <footer className="attribution">AR engine © 2026 Niantic Spatial, Inc.</footer>
        </div>
      )}

      {phase === 'starting' && <div className="center-note">Starting camera…</div>}

      {(phase === 'ar' || phase === 'viewer') && (
        <div className="hud">
          <div className="hud-top">
            <span className="pill">{anchorName}</span>
            {phase === 'ar' && placed && (
              <span className="hud-actions">
                <button className="pill btn-pill" onClick={toggleTabletop}>
                  {tabletop ? 'Real size' : 'Tabletop'}
                </button>
                <button className="pill btn-pill" onClick={replace}>
                  Re-place
                </button>
              </span>
            )}
          </div>

          {phase === 'ar' && !placed && (
            <div className="hud-center">
              {assetProgress ? (
                <span className="pill">
                  Loading assets {assetProgress[0]}/{assetProgress[1]}…
                </span>
              ) : reticleVisible ? (
                <span className="pill">Tap to place</span>
              ) : (
                <span className="pill">Point your camera at the ground</span>
              )}
            </div>
          )}

          {(placed || phase === 'viewer') && (
            <div className="hud-bottom">
              <div className="cta-card">
                <p>
                  This is 1 of {anchorCount || '?'} checkpoints in <strong>{altName}</strong>
                </p>
                <a
                  className="btn primary"
                  href={SITE_URL}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() =>
                    track('share_cta_clicked', {
                      alternativeId,
                      anchorId,
                      meta: {mode: phase === 'viewer' ? 'viewer' : 'ar'},
                    })
                  }
                >
                  Get the full experience
                </a>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
